// src/components/admin/page-management/section-forms/TestimonialsForm.js
import React, { useState } from 'react';
import ColorPicker from '../../shared/ColorPicker';
import MediaUploader from '../../shared/MediaUploader';
import { Plus,MessageSquare,User,Move,Sparkles,Layout,Palette,Settings,Grid,ArrowLeftRight,Eye, Trash2,Type, ChevronDown, ChevronUp, StarIcon } from 'lucide-react';

const TestimonialsForm = ({ content, updateContent }) => {
  const [expandedTestimonialIndex, setExpandedTestimonialIndex] = useState(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  
  // Initialize testimonials if not present
  const testimonials = content?.testimonials || [
    {
      id: 1,
      name: 'Sarah Johnson',
      company: 'Bright Boutique',
      role: 'Owner',
      content: 'The custom shopping bags exceeded our expectations. Our customers love taking them home and they\'ve become a walking advertisement for our boutique!',
      rating: 5,
      imageUrl: '/images/testimonial1.jpg'
    },
    {
      id: 2,
      name: 'Michael Chen',
      company: 'Organic Delights',
      role: 'Marketing Director',
      content: 'We needed eco-friendly packaging that aligned with our brand values. Bag & Box delivered beautiful kraft paper bags that our customers appreciate for their sustainability.',
      rating: 5,
      imageUrl: '/images/testimonial2.jpg'
    },
    {
      id: 3,
      name: 'Robert Fernandez',
      company: 'Luxury Gifts',
      role: 'CEO',
      content: 'The premium gift boxes we ordered have elevated our entire unboxing experience. The attention to detail and quality of materials is exactly what our luxury brand needed.',
      rating: 5,
      imageUrl: '/images/testimonial3.jpg'
    }
  ];
  
  // Add a new testimonial
  const addTestimonial = () => {
    const newTestimonials = [...testimonials, {
      id: Date.now(), // Generate unique ID
      name: 'New Client',
      company: 'Company Name',
      role: 'Position',
      content: 'Add your testimonial content here...',
      rating: 5,
      imageUrl: ''
    }];
    
    updateContent('testimonials', newTestimonials);
    setExpandedTestimonialIndex(newTestimonials.length - 1);
  };
  
  // Remove a testimonial
  const removeTestimonial = (index) => {
    const newTestimonials = [...testimonials];
    newTestimonials.splice(index, 1);
    updateContent('testimonials', newTestimonials);
    
    if (expandedTestimonialIndex === index) {
      setExpandedTestimonialIndex(null);
    } else if (expandedTestimonialIndex > index) {
      setExpandedTestimonialIndex(expandedTestimonialIndex - 1);
    }
  };
  
  // Update a testimonial property
  const updateTestimonial = (index, property, value) => {
    const newTestimonials = [...testimonials];
    newTestimonials[index] = {
      ...newTestimonials[index],
      [property]: value
    };
    updateContent('testimonials', newTestimonials);
  };
  
  // Move a testimonial up or down
  const moveTestimonial = (index, direction) => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === testimonials.length - 1)
    ) {
      return;
    }
    
    const newTestimonials = [...testimonials];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newTestimonials[index], newTestimonials[newIndex]] = [newTestimonials[newIndex], newTestimonials[index]];
    
    updateContent('testimonials', newTestimonials);
    
    // Update expanded index if needed
    if (expandedTestimonialIndex === index) {
      setExpandedTestimonialIndex(newIndex);
    } else if (expandedTestimonialIndex === newIndex) {
      setExpandedTestimonialIndex(index);
    }
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Testimonials Section</h3>
      
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-blue-100 p-2 rounded-lg mr-3">
      <Type className="h-5 w-5 text-blue-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Section Content</h4>
      <p className="text-sm text-gray-500 mt-1">Configure the main heading and description for your section</p>
    </div>
  </div>
  
  <div className="space-y-6">
    {/* Text Content */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Text Content</h5>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Section Title</label>
          <input
            type="text"
            value={content?.title || 'What Our Clients Say'}
            onChange={(e) => updateContent('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Enter the main section title"
          />
          <p className="text-xs text-gray-500">The main heading that appears at the top of your section</p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Section Subtitle</label>
          <textarea
            value={content?.subtitle || 'Hear from businesses who have transformed their packaging with us'}
            onChange={(e) => updateContent('subtitle', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
            rows="3"
            placeholder="Enter a descriptive subtitle (optional)"
          />
          <p className="text-xs text-gray-500">Optional subtitle that provides more context about your section</p>
        </div>
      </div>
    </div>

    {/* Text Colors */}
    <div className="border-t border-gray-200 pt-6">
      <h5 className="font-medium text-gray-900 mb-4">Text Colors</h5>
      <p className="text-sm text-gray-600 mb-4">Customize the colors for your section headings</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Title Color</label>
          <div className="flex items-center space-x-3">
            <ColorPicker
              color={content?.titleColor || '#111827'}
              onChange={(color) => updateContent('titleColor', color)}
            />
            <div 
              className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
              style={{ backgroundColor: content?.titleColor || '#111827' }}
            />
          </div>
          <p className="text-xs text-gray-500">Color for the main section title</p>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Subtitle Color</label>
          <div className="flex items-center space-x-3">
            <ColorPicker
              color={content?.subtitleColor || '#4b5563'}
              onChange={(color) => updateContent('subtitleColor', color)}
            />
            <div 
              className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
              style={{ backgroundColor: content?.subtitleColor || '#4b5563' }}
            />
          </div>
          <p className="text-xs text-gray-500">Color for the subtitle text</p>
        </div>
      </div>
    </div>
  </div>
</div>
    
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-amber-100 p-2 rounded-lg mr-3">
      <Layout className="h-5 w-5 text-amber-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Layout & Display</h4>
      <p className="text-sm text-gray-500 mt-1">Configure how your content is displayed and organized</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Layout Type */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Layout Type</h5>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Display Style</label>
        <select
          value={content?.layout || 'grid'}
          onChange={(e) => updateContent('layout', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        >
          <option value="grid">Grid Layout</option>
          <option value="slider">Slider/Carousel</option>
        </select>
        <p className="text-xs text-gray-500">
          {content?.layout === 'grid' 
            ? 'Display items in a responsive grid with multiple columns'
            : 'Display items in a sliding carousel format'
          }
        </p>
      </div>
    </div>

    {/* Grid Layout Settings */}
    {content?.layout === 'grid' && (
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center mb-4">
          <div className="bg-blue-100 p-2 rounded-lg mr-3">
            <Grid className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h5 className="font-medium text-gray-900">Grid Settings</h5>
            <p className="text-sm text-gray-500">Configure responsive grid columns</p>
          </div>
        </div>
        
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Number of cards per row on different screen sizes</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Mobile (phones)</label>
              <select
                value={content?.cardsPerRow?.mobile || 1}
                onChange={(e) => updateContent('cardsPerRow', {
                  ...content?.cardsPerRow,
                  mobile: parseInt(e.target.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value={1}>1 Card</option>
                <option value={2}>2 Cards</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Tablet</label>
              <select
                value={content?.cardsPerRow?.tablet || 2}
                onChange={(e) => updateContent('cardsPerRow', {
                  ...content?.cardsPerRow,
                  tablet: parseInt(e.target.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value={1}>1 Card</option>
                <option value={2}>2 Cards</option>
                <option value={3}>3 Cards</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Desktop</label>
              <select
                value={content?.cardsPerRow?.desktop || 3}
                onChange={(e) => updateContent('cardsPerRow', {
                  ...content?.cardsPerRow,
                  desktop: parseInt(e.target.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value={1}>1 Card</option>
                <option value={2}>2 Cards</option>
                <option value={3}>3 Cards</option>
                <option value={4}>4 Cards</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Slider Layout Settings */}
    {content?.layout === 'slider' && (
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center mb-4">
          <div className="bg-purple-100 p-2 rounded-lg mr-3">
            <ArrowLeftRight className="h-4 w-4 text-purple-600" />
          </div>
          <div>
            <h5 className="font-medium text-gray-900">Slider Settings</h5>
            <p className="text-sm text-gray-500">Configure carousel behavior and controls</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Autoplay Settings */}
          <div>
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="autoplay-enabled"
                  checked={content?.autoplay !== false}
                  onChange={(e) => updateContent('autoplay', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="autoplay-enabled" className="text-sm font-medium text-gray-700">Enable Autoplay</label>
                <p className="text-xs text-gray-500 mt-1">Automatically advance slides without user interaction</p>
              </div>
            </div>

            {content?.autoplay !== false && (
              <div className="pl-7 space-y-2">
                <label className="block text-sm font-medium text-gray-700">Autoplay Speed</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    value={content?.autoplaySpeed || 5000}
                    onChange={(e) => updateContent('autoplaySpeed', parseInt(e.target.value))}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    min="1000"
                    max="10000"
                    step="500"
                  />
                  <span className="text-sm text-gray-500">milliseconds</span>
                </div>
                <p className="text-xs text-gray-500">
                  Time between slides (1000ms = 1 second). Recommended: 3000-7000ms
                </p>
              </div>
            )}
          </div>

          {/* Slider Controls */}
          <div>
            <h6 className="font-medium text-gray-900 mb-3">Slider Controls</h6>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="slider-controls"
                    checked={content?.showSliderControls !== false}
                    onChange={(e) => updateContent('showSliderControls', e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="slider-controls" className="text-sm font-medium text-gray-700">Show Arrow Controls</label>
                  <p className="text-xs text-gray-500 mt-1">Display left/right arrow buttons for manual navigation</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="slider-dots"
                    checked={content?.showSliderDots !== false}
                    onChange={(e) => updateContent('showSliderDots', e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="slider-dots" className="text-sm font-medium text-gray-700">Show Navigation Dots</label>
                  <p className="text-xs text-gray-500 mt-1">Display dot indicators showing current slide position</p>
                </div>
              </div>
            </div>

            {/* Dots Color Settings */}
            {content?.showSliderDots !== false && (
              <div className="mt-4 pl-7">
                <h6 className="font-medium text-gray-900 mb-3">Dot Colors</h6>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Inactive Dots</label>
                    <div className="flex items-center space-x-3">
                      <ColorPicker
                        color={content?.sliderDotsColor || '#d1d5db'}
                        onChange={(color) => updateContent('sliderDotsColor', color)}
                      />
                      <div 
                        className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                        style={{ backgroundColor: content?.sliderDotsColor || '#d1d5db' }}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Active Dot</label>
                    <div className="flex items-center space-x-3">
                      <ColorPicker
                        color={content?.sliderDotsActiveColor || '#3b82f6'}
                        onChange={(color) => updateContent('sliderDotsActiveColor', color)}
                      />
                      <div 
                        className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                        style={{ backgroundColor: content?.sliderDotsActiveColor || '#3b82f6' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Display Options */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-green-100 p-2 rounded-lg mr-3">
          <Eye className="h-4 w-4 text-green-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Display Options</h5>
          <p className="text-sm text-gray-500">Control which elements are shown</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start space-x-3">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="show-images"
                checked={content?.showImages !== false}
                onChange={(e) => updateContent('showImages', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="show-images" className="text-sm font-medium text-gray-700">Show Client Images</label>
              <p className="text-xs text-gray-500 mt-1">Display profile photos or avatars</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="show-rating"
                checked={content?.showRating !== false}
                onChange={(e) => updateContent('showRating', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="show-rating" className="text-sm font-medium text-gray-700">Show Star Ratings</label>
              <p className="text-xs text-gray-500 mt-1">Display star rating system</p>
            </div>
          </div>
        </div>

        {/* Image Shape Settings */}
        {content?.showImages !== false && (
          <div className="pl-7 space-y-2">
            <label className="block text-sm font-medium text-gray-700">Image Shape</label>
            <select
              value={content?.imageShape || 'circle'}
              onChange={(e) => updateContent('imageShape', e.target.value)}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="circle">Circular</option>
              <option value="square">Square</option>
            </select>
            <p className="text-xs text-gray-500">How client profile images should be displayed</p>
          </div>
        )}

        {/* Quote Icon Settings */}
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="show-quote-icon"
                checked={content?.showQuoteIcon !== false}
                onChange={(e) => updateContent('showQuoteIcon', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="show-quote-icon" className="text-sm font-medium text-gray-700">Show Quote Icon</label>
            </div>
          </div>

          {content?.showQuoteIcon !== false && (
            <div className="pl-7 space-y-2">
              <label className="block text-sm font-medium text-gray-700">Quote Icon Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.quoteIconColor || 'rgba(0, 0, 0, 0.1)'}
                  onChange={(color) => updateContent('quoteIconColor', color)}
                />
                
              </div>
            </div>
          )}
        </div>

        {/* Divider Settings */}
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="show-divider"
                checked={content?.showDivider !== false}
                onChange={(e) => updateContent('showDivider', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="show-divider" className="text-sm font-medium text-gray-700">Show Title Divider</label>
            </div>
          </div>

          {content?.showDivider !== false && (
            <div className="pl-7 space-y-2">
              <label className="block text-sm font-medium text-gray-700">Divider Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.dividerColor || '#e5e7eb'}
                  onChange={(color) => updateContent('dividerColor', color)}
                />
                
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
</div>

<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-rose-100 p-2 rounded-lg mr-3">
      <Palette className="h-5 w-5 text-rose-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Colors & Styling</h4>
      <p className="text-sm text-gray-500 mt-1">Customize the visual appearance and color scheme</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Background Color */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Background</h5>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Section Background Color</label>
        <div className="flex items-center space-x-3">
          <ColorPicker
            color={content?.backgroundColor || '#ffffff'}
            onChange={(color) => updateContent('backgroundColor', color)}
          />
          
        </div>
        <p className="text-xs text-gray-500">Background color for the entire section</p>
      </div>
    </div>

    {/* Advanced Settings Toggle */}
    <div className="border-t border-gray-200 pt-6">
      <button
        type="button"
        className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
        onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
      >
        <Settings className="h-4 w-4 mr-2" />
        {showAdvancedSettings ? 'Hide Advanced Colors' : 'Show Advanced Colors'}
        {showAdvancedSettings ? (
          <ChevronUp className="h-4 w-4 ml-2" />
        ) : (
          <ChevronDown className="h-4 w-4 ml-2" />
        )}
      </button>
    </div>

    {/* Advanced Settings */}
    {showAdvancedSettings && (
      <div className="space-y-6 pt-4 border-t border-gray-200">
        {/* Card Colors */}
        <div>
          <h5 className="font-medium text-gray-900 mb-4">Card Colors</h5>
          <p className="text-sm text-gray-600 mb-4">Customize the appearance of individual testimonial cards</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Card Background</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.cardBackgroundColor || '#f9fafb'}
                  onChange={(color) => updateContent('cardBackgroundColor', color)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.cardBackgroundColor || '#f9fafb' }}
                />
              </div>
              <p className="text-xs text-gray-500">Background color for testimonial cards</p>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Card Border</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.cardBorderColor || '#e5e7eb'}
                  onChange={(color) => updateContent('cardBorderColor', color)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.cardBorderColor || '#e5e7eb' }}
                />
              </div>
              <p className="text-xs text-gray-500">Border color around each card</p>
            </div>
          </div>
        </div>

        {/* Text Colors */}
        <div>
          <h5 className="font-medium text-gray-900 mb-4">Text Colors</h5>
          <p className="text-sm text-gray-600 mb-4">Control the colors of different text elements</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Main Text Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.textColor || '#1f2937'}
                  onChange={(color) => updateContent('textColor', color)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.textColor || '#1f2937' }}
                />
              </div>
              <p className="text-xs text-gray-500">Color for testimonial text content</p>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Company/Role Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.companyColor || '#6b7280'}
                  onChange={(color) => updateContent('companyColor', color)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.companyColor || '#6b7280' }}
                />
              </div>
              <p className="text-xs text-gray-500">Color for company names and job titles</p>
            </div>
          </div>
        </div>

        {/* Interactive Elements */}
        <div>
          <h5 className="font-medium text-gray-900 mb-4">Interactive Elements</h5>
          <p className="text-sm text-gray-600 mb-4">Colors for ratings, buttons, and interactive components</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Star Rating Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.starColor || '#f59e0b'}
                  onChange={(color) => updateContent('starColor', color)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.starColor || '#f59e0b' }}
                />
              </div>
              <p className="text-xs text-gray-500">Color for filled star ratings</p>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Button Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.buttonColor || '#6b7280'}
                  onChange={(color) => updateContent('buttonColor', color)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.buttonColor || '#6b7280' }}
                />
              </div>
              <p className="text-xs text-gray-500">Default color for buttons and controls</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Button Hover Color</label>
            <div className="flex items-center space-x-3">
              <ColorPicker
                color={content?.buttonHoverColor || '#4b5563'}
                onChange={(color) => updateContent('buttonHoverColor', color)}
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.buttonHoverColor || '#4b5563' }}
              />
            </div>
            <p className="text-xs text-gray-500">Color when hovering over buttons</p>
          </div>
        </div>

        {/* Card Styling */}
        <div>
          <h5 className="font-medium text-gray-900 mb-4">Card Styling</h5>
          <p className="text-sm text-gray-600 mb-4">Control the shape and visual effects of testimonial cards</p>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Card Border Radius</label>
              <input
                type="text"
                value={content?.cardBorderRadius || '0.75rem'}
                onChange={(e) => updateContent('cardBorderRadius', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="e.g., 0.75rem, 12px, 0"
              />
              <p className="text-xs text-gray-500">Controls how rounded the card corners are</p>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="card-shadow"
                  checked={content?.cardShadow !== false}
                  onChange={(e) => updateContent('cardShadow', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="card-shadow" className="text-sm font-medium text-gray-700">Show Card Shadow</label>
                <p className="text-xs text-gray-500 mt-1">Add drop shadows to testimonial cards for depth</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
</div>

<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-emerald-100 p-2 rounded-lg mr-3">
      <Move className="h-5 w-5 text-emerald-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Spacing & Animation</h4>
      <p className="text-sm text-gray-500 mt-1">Control section spacing and smooth animations</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Spacing Settings */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Section Spacing</h5>
      <p className="text-sm text-gray-600 mb-4">Adjust the space around the testimonials section</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Top Padding</label>
          <input
            type="text"
            value={content?.paddingTop || '4rem'}
            onChange={(e) => updateContent('paddingTop', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="e.g., 4rem, 64px"
          />
          <p className="text-xs text-gray-500">Space above the section</p>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Bottom Padding</label>
          <input
            type="text"
            value={content?.paddingBottom || '4rem'}
            onChange={(e) => updateContent('paddingBottom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="e.g., 4rem, 64px"
          />
          <p className="text-xs text-gray-500">Space below the section</p>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Bottom Margin</label>
          <input
            type="text"
            value={content?.marginBottom || '3rem'}
            onChange={(e) => updateContent('marginBottom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="e.g., 3rem, 48px"
          />
          <p className="text-xs text-gray-500">External space after section</p>
        </div>
      </div>
    </div>

    {/* Animation Settings */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-violet-100 p-2 rounded-lg mr-3">
          <Sparkles className="h-5 w-5 text-violet-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Animation Settings</h5>
          <p className="text-sm text-gray-500">Add smooth entrance animations to testimonials</p>
        </div>
      </div>

      <div className="flex items-start space-x-3">
        <div className="flex items-center h-5">
          <input
            type="checkbox"
            id="animation-enabled"
            checked={content?.animationEnabled !== false}
            onChange={(e) => updateContent('animationEnabled', e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="animation-enabled" className="text-sm font-medium text-gray-700">Enable Animations</label>
          <p className="text-xs text-gray-500 mt-1">Add fade-in animations when testimonial cards come into view</p>
        </div>
      </div>

      {/* Animation Info */}
      {content?.animationEnabled !== false && (
        <div className="mt-4 p-4 bg-violet-50 border border-violet-200 rounded-lg">
          <div className="flex items-center mb-2">
            <div className="w-2 h-2 bg-violet-500 rounded-full mr-2"></div>
            <span className="text-sm font-medium text-violet-800">Animation Effects</span>
          </div>
          <ul className="text-xs text-violet-700 space-y-1">
            <li>• Testimonials fade in smoothly as they scroll into view</li>
            <li>• Cards appear with a subtle staggered timing</li>
            <li>• Enhances user engagement and visual appeal</li>
          </ul>
        </div>
      )}
    </div>
  </div>
</div>
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-cyan-100 p-2 rounded-lg mr-3">
      <MessageSquare className="h-5 w-5 text-cyan-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Testimonials</h4>
      <p className="text-sm text-gray-500 mt-1">Add and manage customer testimonials and reviews</p>
    </div>
  </div>

  <div className="space-y-4">
    {testimonials.map((testimonial, index) => (
      <div 
        key={testimonial.id || index} 
        className="border border-gray-200 rounded-xl bg-gray-50 overflow-hidden hover:shadow-sm transition-shadow duration-200"
      >
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
          onClick={() => setExpandedTestimonialIndex(expandedTestimonialIndex === index ? null : index)}
        >
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <User className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <span className="font-medium text-gray-900">
                {testimonial.name || `Testimonial ${index + 1}`}
              </span>
              {testimonial.company && (
                <p className="text-sm text-gray-500 mt-1">
                  {testimonial.company}
                  {testimonial.role && ` • ${testimonial.role}`}
                </p>
              )}
              {testimonial.content && (
                <p className="text-sm text-gray-600 mt-1 line-clamp-1">
                  "{testimonial.content.substring(0, 80)}
                  {testimonial.content.length > 80 ? '...' : ''}"
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                moveTestimonial(index, 'up');
              }}
              disabled={index === 0}
              title="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                moveTestimonial(index, 'down');
              }}
              disabled={index === testimonials.length - 1}
              title="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            
            <button
              type="button"
              className="p-2 text-red-400 hover:text-red-600 hover:bg-white rounded-lg transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                removeTestimonial(index);
              }}
              title="Delete testimonial"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            
            <div className="p-2">
              {expandedTestimonialIndex === index ? 
                <ChevronUp className="h-4 w-4 text-gray-600" /> : 
                <ChevronDown className="h-4 w-4 text-gray-600" />}
            </div>
          </div>
        </div>
        
        {expandedTestimonialIndex === index && (
          <div className="bg-white border-t border-gray-200 p-6">
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h5 className="font-medium text-gray-900 mb-4">Client Information</h5>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Client Name</label>
                    <input
                      type="text"
                      value={testimonial.name || ''}
                      onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter client's full name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Company</label>
                      <input
                        type="text"
                        value={testimonial.company || ''}
                        onChange={(e) => updateTestimonial(index, 'company', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Company name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Role/Position</label>
                      <input
                        type="text"
                        value={testimonial.role || ''}
                        onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Job title or role"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="border-t border-gray-200 pt-6">
                <h5 className="font-medium text-gray-900 mb-4">Testimonial Content</h5>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Testimonial Text</label>
                    <textarea
                      value={testimonial.content || ''}
                      onChange={(e) => updateTestimonial(index, 'content', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      rows="4"
                      placeholder="Enter the client's testimonial or review..."
                    />
                    <p className="text-xs text-gray-500">
                      {testimonial.content ? `${testimonial.content.length} characters` : 'No content yet'}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Star Rating</label>
                    <div className="flex items-center space-x-3">
                      <select
                        value={testimonial.rating || 5}
                        onChange={(e) => updateTestimonial(index, 'rating', parseInt(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      >
                        <option value={1}>⭐ 1 Star</option>
                        <option value={2}>⭐⭐ 2 Stars</option>
                        <option value={3}>⭐⭐⭐ 3 Stars</option>
                        <option value={4}>⭐⭐⭐⭐ 4 Stars</option>
                        <option value={5}>⭐⭐⭐⭐⭐ 5 Stars</option>
                      </select>
                      <span className="text-sm text-gray-500">
                        {testimonial.rating === 5 ? 'Excellent' : 
                         testimonial.rating === 4 ? 'Very Good' : 
                         testimonial.rating === 3 ? 'Good' : 
                         testimonial.rating === 2 ? 'Fair' : 'Poor'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Client Image */}
              <div className="border-t border-gray-200 pt-6">
                <h5 className="font-medium text-gray-900 mb-4">Client Photo</h5>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Profile Image</label>
                  <MediaUploader
                    type="image"
                    value={testimonial.imageUrl || ''}
                    onUploadComplete={(url) => updateTestimonial(index, 'imageUrl', url)}
                    onRemove={() => updateTestimonial(index, 'imageUrl', '')}
                    className="mb-2"
                  />
                  <p className="text-xs text-gray-500">
                    Upload a professional photo of the client (optional but recommended)
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
  
  <button
    type="button"
    className="mt-6 flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:text-gray-800 hover:border-gray-400 hover:bg-gray-50 transition-all duration-200"
    onClick={addTestimonial}
  >
    <Plus className="h-5 w-5 mr-2" />
    Add New Testimonial
  </button>
</div>

      
    </div>
  );
};

export default TestimonialsForm;